import type { App, Plugin, SetupContext, EmitsOptions } from 'vue'

/* 生成install函数 */

export type SFCWithInstall<T> = T & Plugin

export type SFCInstallWithContext<T> = SFCWithInstall<T> & {
	_context: App | null
}

export type EmitFn<E extends EmitsOptions> = SetupContext<E>['emit']

export const withInstall = <T, E extends Record<string, any>>(main: T, extra?: E) => {
	;(main as SFCWithInstall<T>).install = (app: App): void => {
		for (const comp of [main, ...Object.values(extra ?? {})]) {
			app.component(comp.name, comp)
		}
	}

	if (extra) {
		for (const [key, comp] of Object.entries(extra)) {
			;(main as any)[key] = comp
		}
	}
	return main as SFCWithInstall<T> & E
}

export const withInstallFunction = <T>(fn: T, name: string) => {
	;(fn as SFCWithInstall<T>).install = (app: App) => {
		;(fn as SFCInstallWithContext<T>)._context = app
		app.config.globalProperties[name] = fn
	}

	return fn as SFCInstallWithContext<T>
}

export function isNumber(value) {
	return typeof value === 'number' && isFinite(value)
}

export function isNumeric(value) {
	return !isNaN(parseFloat(value)) && isFinite(value)
}

export function isString(obj) {
	return Object.prototype.toString.call(obj) === '[object String]'
}

export function isBoolean(value) {
	return typeof value === 'boolean'
}

export function isDateObj(value) {
	return Object.prototype.toString.call(value) === '[object Date]'
}

export function isObject(obj) {
	return Object.prototype.toString.call(obj) === '[object Object]'
}

export function isArray(obj) {
	return Object.prototype.toString.call(obj) === '[object Array]'
}

export function isHtmlElement(node) {
	return node && node.nodeType === Node.ELEMENT_NODE
}

export const isFunction = functionToCheck => {
	const getType = {}
	return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]'
}

export const isUndefined = val => {
	return val === void 0
}

export const isDefined = val => {
	return val !== undefined && val !== null
}

export function isPromise(val) {
	return isObject(val) && isFunction(val.then) && isFunction(val.catch)
}
